import bubbleUntil from './Functions/bubbleUntil';

const ignoreSelector = ':not([tabindex^="-"]):not([disabled])';
const focusableSelector =
    [
        'a[href]',
        'area[href]',
        'input',
        'select',
        'textarea',
        'button',
        'iframe',
        'object',
        'embed',
        '[contenteditable]',
        '[tabindex]',
    ].join(ignoreSelector + ',') + ignoreSelector;
const key = {
    tab: 9,
    escape: 27,
};

class TouchAndMobile {
    constructor({ namespace, header, setTop }) {
        if (!namespace || !header) {
            return;
        }
        this.header = document.querySelector(header);

        if (!this.header) {
            return;
        }
        this.windowResizeTimeout = null;
        this.menuOpenClass = '-menuopen';
        this.openSubMenuOnTap = `.${namespace}__link--level-1.${namespace}__link--sub`;
        this.anchorLinks = this.header.querySelectorAll('a[href*="#"');
        this.navigation = this.header.querySelector(`.${namespace}`);
        this.hamburger = document.querySelector(`.header__hamburger-button`);

        // Save all focusable into a variable
        // We need to filter the not visible out
        this.focusableRaw = [...this.header.querySelectorAll(focusableSelector)];
        this.focusable = [];

        this.focusableNavRaw = [];
        this.focusableNav = [];
        this.touchSubmenuLinks = [];
        this.isTouch = 'ontouchstart' in document.documentElement && this.openSubMenuOnTap;
        this.htmlElement = document.documentElement;
        this.bodyElement = document.body;
        this.setTop = setTop;
        this.menuIsOpen = this.htmlElement.classList.contains(this.menuOpenClass);
        this.mobileMenuIsActive = false;

        // save the event handler so it can be used in multiple places
        this.maintainFocusHandler = this.maintainFocus.bind(this);
        this.bindKeypressHandler = this.bindKeypress.bind(this);

        if (this.navigation) {
            this.focusableNavRaw = [...this.navigation.querySelectorAll(focusableSelector)];
            this.focusableNav = this.filterFocusable(this.focusableNavRaw);

            if (this.isTouch) {
                this.touchSubmenuLinks = [...this.navigation.querySelectorAll(this.openSubMenuOnTap)];
            }
        }

        this.setFocusable();
        this.checkVisibilityOfMenu();
        this.setVisibility();
        this.addEvents();

        return {
            // boolean if the menu is on a touch device
            isTouch: this.isTouch,
            // returns a boolean if the mobile menu is open
            isOpen: () => {
                return this.menuIsOpen;
            },
            // open the mobile menu and return true.If it is already open or not visible it returns false
            open: this.openMenu.bind(this),
            // close the mobile menu and return true.If it is already closed it returns false
            close: this.closeMenu.bind(this),
            // toggle the menu(closes if open, open it if it is closed) return boolean status of the menu
            toggle: this.toggleMenu.bind(this),
        };
    }

    setFocusable() {
        this.focusable = this.filterFocusable(this.focusableRaw);
        if (this.navigation) {
            this.focusableNav = this.filterFocusable(this.focusableNavRaw);
        }
    }

    filterFocusable(arrayOfElements) {
        return arrayOfElements.filter((element) => this.checkVisibility(element));
    }

    checkVisibilityOfMenu() {
        if (this.hamburger) {
            this.mobileMenuIsActive = this.checkVisibility(this.hamburger);
        }
    }

    checkVisibility(element) {
        let style = window.getComputedStyle(element);
        if (
            style.display == 'none' ||
            style.opacity == '0' ||
            style.visibility == 'hidden' ||
            element.offsetParent === null
        ) {
            return false;
        }

        return true;
    }

    setVisibility() {
        if (!this.mobileMenuIsActive) {
            this.closeMenu(false);
            this.setTabIndexFromNavigationElements(true);
        } else if (!this.menuIsOpen) {
            this.setTabIndexFromNavigationElements(false);
        }
    }

    closeMenu(setIndex = true) {
        if (!this.menuIsOpen) {
            return false;
        }
        this.menuIsOpen = false;
        if (setIndex) {
            this.setTabIndexFromNavigationElements(false);
        }
        this.htmlElement.classList.remove(this.menuOpenClass);
        this.bodyElement.classList.remove(this.menuOpenClass);
        if (this.setTop) {
            window.scrollTo(0, parseInt(this.bodyElement.style.top) * -1);
            this.bodyElement.style.top = '';
        }

        // Remove events
        try {
            this.bodyElement.removeEventListener('focus', this.maintainFocusHandler);
        } catch (error) {}
        try {
            document.removeEventListener('keydown', this.bindKeypressHandler);
        } catch (error) {}

        this.triggerEvent('mobileMenu.close');
        return true;
    }

    setTabIndexFromNavigationElements(enable) {
        this.focusableNav.forEach((element) => {
            element.tabIndex = enable ? 0 : -1;
        });
    }

    addEvents() {
        window.addEventListener('resize', () => {
            if (this.windowResizeTimeout) {
                window.cancelAnimationFrame(this.windowResizeTimeout);
            }

            this.windowResizeTimeout = window.requestAnimationFrame(() => {
                this.checkVisibilityOfMenu();
                this.setVisibility();
                this.setFocusable();
            });
        });

        this.hamburger.addEventListener('click', () => {
            this.toggleMenu();
            this.hamburger.blur();
        });

        [...this.anchorLinks].forEach((anchorLink) => {
            anchorLink.addEventListener('click', () => {
                this.toggleMenu();
                this.hamburger.blur();
            });
        });

        if (this.isTouch) {
            this.htmlElement.addEventListener('touchend', (event) => {
                if (!this.menuIsOpen) {
                    let link = bubbleUntil(event.target, this.openSubMenuOnTap);
                    if (link) {
                        if (document.activeElement != link) {
                            event.preventDefault();
                            event.stopPropagation();
                            this.closeSubmenus();
                            setTimeout(() => {
                                link.focus();
                            }, 0);
                        }
                    } else {
                        this.closeSubmenus();
                    }
                }
            });
        }
    }

    closeSubmenus() {
        this.touchSubmenuLinks.forEach((link) => {
            link.blur();
        });
    }

    maintainFocus(event) {
        // If the menu is shown and the focus is not within the header,
        // move it back to its first focusable child
        if (this.menuIsOpen && !this.header.contains(event.target) && this.focusable.length) {
            this.focusable[0].focus();
        }
    }

    bindKeypress(event) {
        if (this.menuIsOpen) {
            // If ESCAPE key is being pressed, prevent any further
            // effects from the ESCAPE key and hide the Menu

            if (event.which === key.escape) {
                event.preventDefault();
                this.closeMenu();
            }

            // If TAB key is being pressed, make sure the
            // focus stays trapped within the menu
            if (event.which === key.tab) {
                this.trapTabKey(event);
            }
        }
    }

    trapTabKey(event) {
        let index = this.focusable.indexOf(document.activeElement);

        // If the SHIFT key is being pressed while tabbing (moving backwards) and
        // the currently focused item is the first one, move the focus to the last
        // focusable item from the menu
        if (event.shiftKey && index === 0) {
            this.focusable[this.focusable.length - 1].focus();
            event.preventDefault();

            // If the SHIFT key is not being pressed (moving forwards) and the
            // currently focused item is the last one, move the focus to the
            // first focusable item from the menu
        } else if (!event.shiftKey && index === this.focusable.length - 1) {
            this.focusable[0].focus();
            event.preventDefault();
        }
    }

    triggerEvent(eventName) {
        let event;
        const options = {
            header: this.header,
            hamburger: this.hamburger,
            navigation: this.navigation,
            focusable: this.focusable,
            focusableNav: this.focusableNav,
        };
        if (window.CustomEvent) {
            event = new CustomEvent(eventName, { detail: options });
        } else {
            event = document.createEvent('CustomEvent');
            event.initCustomEvent(eventName, true, true, options);
        }
        document.dispatchEvent(event);
    }

    openMenu() {
        if (!this.mobileMenuIsActive || this.menuIsOpen) {
            return false;
        }
        this.menuIsOpen = true;

        this.setTabIndexFromNavigationElements(true);
        if (this.setTop) {
            this.bodyElement.style.top = `-${window.pageYOffset}px`;
        }
        this.htmlElement.classList.add(this.menuOpenClass);
        this.bodyElement.classList.add(this.menuOpenClass);

        // Add events
        this.bodyElement.addEventListener('focus', this.maintainFocusHandler);
        document.addEventListener('keydown', this.bindKeypressHandler);

        // Set focus on first element and blur it
        const firstFocusElement = this.focusable[0];
        if (firstFocusElement) {
            firstFocusElement.focus();
            firstFocusElement.blur();
        }

        this.triggerEvent('mobileMenu.open');
        return true;
    }

    toggleMenu() {
        if (!this.mobileMenuIsActive) {
            return null;
        }
        if (this.menuIsOpen) {
            this.closeMenu();
        } else {
            this.openMenu();
        }
        return this.menuIsOpen;
    }
}

export default TouchAndMobile;
